import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../../../services/ApiService';
import Loader from '../../../../components/Loader';
import ImageUpload from '../../../../components/ImageUpload';

const ServiceForm = () => {
  const { partnerId, id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    price: '',
    price_type: 'per_day',
    image: null,
  });

  useEffect(() => {
    if (id) getSingleData();
  }, [id]);

  const getSingleData = async () => {
    try {
      setPageLoader(true);
      const response = await ApiService.request({ method: 'GET', url: `admin/services/${id}` });
      const data = response.data;
      if (data.status) {
        const svc = data.data;
        setFormData({
          title:         svc.title || '',
          title_ar:      svc.title_ar || '',
          description:   svc.description || '',
          description_ar:svc.description_ar || '',
          price:         svc.price ?? '',
          price_type:    svc.price_type || 'per_day',
          image:         svc.image || null,
        });
      } else {
        toast.error(data.message);
      }
    } catch (_) {}
    finally { setPageLoader(false); }
  };

  const handleImageChange = (_, file) => setFormData(prev => ({ ...prev, image: file }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoader(true);
    try {
      const form = new FormData();
      form.append('partner_id', partnerId);
      form.append('title', formData.title);
      if (formData.title_ar)        form.append('title_ar', formData.title_ar);
      if (formData.description)     form.append('description', formData.description);
      if (formData.description_ar)  form.append('description_ar', formData.description_ar);
      if (formData.price !== '')    form.append('price', formData.price);
      form.append('price_type', formData.price_type);
      if (formData.image instanceof File) form.append('image', formData.image);

      const url = id
        ? `admin/partners/${partnerId}/services/createOrUpdate/${id}`
        : `admin/partners/${partnerId}/services/createOrUpdate`;

      const response = await ApiService.request({ method: 'POST', url, data: form });
      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        navigate(`/admin/partners/${partnerId}?tab=services`);
      } else {
        toast.error(data.message);
      }
    } catch (_) {}
    finally { setIsLoader(false); }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit' : 'Add'} Service
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <a href="/dashboard" className="text-muted text-hover-primary">Dashboard</a>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/partners" className="text-muted text-hover-primary">partners</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to={`/admin/partners/${partnerId}`} className="text-muted text-hover-primary">partner Details</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-6">
              {pageLoader ? (
                <div className="text-center py-10"><Loader size={30} text="Loading" color="black" /></div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-9">

                    {/* Title */}
                    <div className="col-md-6 fv-row">
                      <label className="required fs-6 fw-semibold mb-2">Title</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Service title"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>

                    {/* Title AR */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Title (Arabic)</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="عنوان الخدمة"
                        dir="rtl"
                        value={formData.title_ar}
                        onChange={(e) => setFormData(prev => ({ ...prev, title_ar: e.target.value }))}
                      />
                    </div>

                    {/* Description */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Description</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder="Service description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>

                    {/* Description AR */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Description (Arabic)</label>
                      <textarea
                        className="form-control form-control-solid"
                        placeholder="وصف الخدمة"
                        dir="rtl"
                        value={formData.description_ar}
                        onChange={(e) => setFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                      />
                    </div>

                    {/* Price */}
                    <div className="col-md-3 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Price</label>
                      <div className="input-group">
                        <span className="input-group-text bg-light border-0 text-muted">AED</span>
                        <input
                          type="number"
                          className="form-control form-control-solid"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Price Type */}
                    <div className="col-md-3 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Price Type</label>
                      <select
                        className="form-select form-select-solid"
                        value={formData.price_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, price_type: e.target.value }))}
                      >
                        <option value="per_hour">Per Hour</option>
                        <option value="per_day">Per Day</option>
                        <option value="per_item">Per Item</option>
                      </select>
                    </div>

                    {/* Image */}
                    <div className="col-md-6 fv-row">
                      <label className="fs-6 fw-semibold mb-2">Image</label>
                      <ImageUpload
                        name="image"
                        onImageChange={handleImageChange}
                        defaultPreviewUrl={formData?.image}
                      />
                    </div>

                  </div>

                  {/* Actions */}
                  <div className="text-center mt-9 mb-3">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={() => navigate(`/admin/partners/${partnerId}`)}
                    >
                      Back
                    </button>
                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                      <span hidden={isLoader}>Submit</span>
                      {isLoader && <Loader size={20} text="Submitting" />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceForm;
